import Cookies from 'js-cookie'
import { RefObject, useEffect, useReducer, useState } from 'react'
import {
  Button,
  ChipsSlider,
  GhostInput,
  Logo,
  PaymentLogo
} from '@collegebacker/backer-ui/ui'

import { InfoBubble } from 'components'
import {
  Actions,
  HIDE_CUSTOM_AMOUNT,
  SET_AMOUNT,
  SET_CUSTOM_AMOUNT,
  SET_FREQUENCY,
  SHOW_CUSTOM_AMOUNT
} from 'constants/actions'
import {
  activationDateValue,
  amountValue,
  currentFlowValue,
  frequencyValue
} from 'modules/contribution/LocalState'
import {
  frequencyTextMap,
  generateProjectionDisclaimer,
  getForecastProjection,
  getForecastProjectionString
} from 'modules/contribution/screens/ContributionAmountScreen'
import { NumberFormat } from 'utils'
import { isMobileAppInAppBrowser, validateAmount } from 'utils/helpers'
import { ContributionLimits, Frequency } from 'modules/contribution/types'
import { PartnerName } from 'modules/profile/userInterfaces'
import { ContributionScheduler } from 'modules/contribution/components/ContributionScheduler'

import nc529Logo from 'static/img/partner-logos/nc529.svg'
import styles from './GiftSection.module.scss'

const DEFAULT_AMOUNT = 25_00

const CHIPS_AMOUNTS: AmountsArray = [
  { value: 25_00, label: '$25' },
  { value: 50_00, label: '$50' },
  { value: 100_00, label: '$100' },
  { value: 150_00, label: '$150' },
  { value: 250_00, label: '$250' },
  { value: 500_00, label: '$500' }
]
export interface State {
  showCustomAmount: boolean
  amount: number
  sliderAmount: number
  customAmount: number
  frequency: Frequency
}

function calculateAmountByFrequency(amount: number, frequency: Frequency) {
  if (frequency === 'semimonthly') {
    return amount / 2
  }

  return amount
}

export function reducer(state: State, action: Actions): State {
  const { sliderAmount, customAmount, frequency, amount } = state

  switch (action.type) {
    case SHOW_CUSTOM_AMOUNT:
      amountValue(calculateAmountByFrequency(customAmount, frequency))
      return {
        ...state,
        showCustomAmount: true,
        amount: customAmount
      }

    case HIDE_CUSTOM_AMOUNT:
      amountValue(calculateAmountByFrequency(sliderAmount, frequency))

      return {
        ...state,
        showCustomAmount: false,
        amount: sliderAmount
      }

    case SET_CUSTOM_AMOUNT:
      const payloadCustomAmount = action.payload.amount
      amountValue(calculateAmountByFrequency(payloadCustomAmount, frequency))
      return {
        ...state,
        amount: payloadCustomAmount,
        customAmount: payloadCustomAmount
      }

    case SET_AMOUNT:
      const { amount: payloadAmount } = action.payload
      amountValue(calculateAmountByFrequency(payloadAmount, frequency))
      return {
        ...state,
        amount: payloadAmount,
        customAmount: state.showCustomAmount
          ? payloadAmount
          : state.customAmount,
        sliderAmount: state.showCustomAmount
          ? state.sliderAmount
          : payloadAmount
      }

    case SET_FREQUENCY:
      const payloadFrequency = action.payload.frequency
      frequencyValue(payloadFrequency)

      amountValue(calculateAmountByFrequency(amount, payloadFrequency))
      return {
        ...state,
        frequency: payloadFrequency
      }

    default:
      return state
  }
}

const GiftSection = ({
  defaultFrequency,
  disableFollowFund,
  fund,
  giftingModalRef,
  birthdayReminderModalRef
}: {
  defaultFrequency: Frequency
  disableFollowFund: boolean
  fund: {
    handle: string
    fundOwner: {
      partnerName: PartnerName
    }
    yearsUntilCollege: number
    collegeEntryYear: number
    contributionLimits: ContributionLimits
  }
  giftingModalRef: RefObject<null> | any
  birthdayReminderModalRef: RefObject<null> | any
}) => {
  const { fundOwner } = fund
  const partnerIsNC529 = fundOwner.partnerName === 'nc529'

  const flow = currentFlowValue()

  function getInitialAmount() {
    if (amountValue()) return amountValue()

    return DEFAULT_AMOUNT
  }

  const initialAmount = getInitialAmount()
  const amountInSlider = CHIPS_AMOUNTS.find(
    (item) => item.value === initialAmount
  )
  const initialState: State = {
    showCustomAmount: amountInSlider ? false : true,
    amount: initialAmount,
    sliderAmount: amountInSlider ? initialAmount : CHIPS_AMOUNTS[0].value,
    customAmount: initialAmount,
    frequency: defaultFrequency
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  const { showCustomAmount, amount, frequency } = state

  const [activationDate, setActivationDate] = useState(activationDateValue())
  const [customAmountError, setCustomAmountError] = useState('')

  useEffect(() => {
    amountValue(initialAmount)
  }, [])

  useEffect(() => {
    dispatch({
      type: SET_FREQUENCY,
      payload: {
        frequency: defaultFrequency
      }
    })
  }, [defaultFrequency])

  useEffect(() => {
    const giftInProgressCookie = Cookies.get('giftInProgress')
    if (giftInProgressCookie && !isMobileAppInAppBrowser()) {
      const giftInProgress = JSON.parse(giftInProgressCookie)

      const { amount, frequency, activationDate: activationDateString } = giftInProgress
      const activationDate = new Date(activationDateString)

      dispatch({
        type: SET_FREQUENCY,
        payload: {
          frequency
        }
      })

      // check if the slider includes the amount, if not then show the custom amount input
      const amountInSlider = CHIPS_AMOUNTS.find((item) => item.value === amount)

      dispatch({
        type: amountInSlider ? HIDE_CUSTOM_AMOUNT : SHOW_CUSTOM_AMOUNT
      })

      dispatch({
        type: SET_AMOUNT,
        payload: { amount: frequency === 'semimonthly' ? amount * 2 : amount }
      })

      setActivationDate(activationDate)
      activationDateValue(activationDate)

      Cookies.remove('giftInProgress')
    }
  }, [])

  return (
    <section className={styles.giftSection}>
      <div className={styles.giftSectionContent}>
        <div>
          <div className={styles.giftSectionHeader}>
            <div className={styles.giftSectionHeaderTitle}>
              <h2 className="typo-app-title-medium-adaptive">Give a gift</h2>
              {!partnerIsNC529 ? (
                <Logo
                  className={styles.giftBackerPartnerLogo}
                  type="default"
                  hoverAnimation={false}
                />
              ) : (
                <img
                  src={nc529Logo}
                  alt=""
                  className={styles.giftNC529PartnerLogo}
                />
              )}
            </div>
            <span className="typo-app-body-paragraph">
              {getGiftProjection({
                amount,
                frequency,
                fund
              })}
            </span>
          </div>
          {showCustomAmount ? (
            <div className={styles.customAmountContainer}>
              <button
                onClick={() => dispatch({ type: HIDE_CUSTOM_AMOUNT })}
                type="button"
                className={styles.switchBtnSlider}
              >
                Switch back to set amounts
              </button>
              <GhostInput
                allowCents={true}
                autoFocus={true}
                type="money"
                name="custom-amount"
                label=""
                value={(amount / 100).toString()}
                errorMessage={customAmountError}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setCustomAmountError('')
                  const amount =
                    Number(event.target.value.replace(/,/g, '')) * 100
                  dispatch({
                    type: SET_CUSTOM_AMOUNT,
                    payload: { amount }
                  })
                }}
              />
            </div>
          ) : (
            <div className={styles.sliderContainerWrap}>
              <ChipsSlider
                hideOverlayGradients={true}
                showCaption={false}
                items={CHIPS_AMOUNTS}
                showArrows={false}
                containerClassName={styles.sliderContainer}
                defaultIndex={CHIPS_AMOUNTS.findIndex((item: any) => {
                  return item.value === amount
                })}
                onChange={(index) => {
                  const value = CHIPS_AMOUNTS[index].value
                  dispatch({
                    type: SET_AMOUNT,
                    payload: { amount: value }
                  })
                }}
              />

              <button
                onClick={() => dispatch({ type: SHOW_CUSTOM_AMOUNT })}
                type="button"
                className={styles.switchBtn}
              >
                Enter a custom amount
              </button>
            </div>
          )}
        </div>
        <div>
          <ContributionScheduler
            flow={flow}
            frequency={frequency}
            dispatch={dispatch}
            activationDate={activationDate}
            setActivationDate={setActivationDate}
          />
          <div className={styles.ctaContainer}>
            <div className={styles.topContainer}>
              <Button
                className={styles.contributeBtn}
                label="Contribute"
                onClick={() => {
                  const validationError = validateAmount(
                    amount,
                    frequency,
                    fund.contributionLimits
                  )
                  if (validationError) {
                    setCustomAmountError(validationError)
                  } else {
                    giftingModalRef?.current?.open()
                  }
                }}
              />
              {!disableFollowFund && (
                <Button
                  className={styles.followBtn}
                  label="Get a birthday reminder"
                  mode="outline"
                  icon="calendar"
                  onClick={() => {
                    birthdayReminderModalRef?.current?.open()
                  }}
                />
              )}
            </div>
            <div className={styles.bottomContainer}>
              <div className={styles.logosContainer}>
                <div className={styles.logosInnerContainer}>
                  <PaymentLogo
                    name="visa"
                    className={styles.logosInnerContainer_visa}
                  />
                  <PaymentLogo
                    name="mastercard-nocaption"
                    className={styles.logosInnerContainer_mastercard}
                  />
                  <PaymentLogo
                    name="discover"
                    className={styles.logosInnerContainer_discover}
                  />
                  <PaymentLogo
                    name="apple-pay"
                    className={styles.logosInnerContainer_apple}
                  />
                  <PaymentLogo
                    name="g-pay"
                    className={styles.logosInnerContainer_gpay}
                  />
                </div>
              </div>
              {partnerIsNC529 && (
                <div className={styles.logosPoweredByBackerContainer}>
                  <p className="typo-app-body-caption">Powered by </p>
                  <Logo
                    className={styles.logosPoweredByBackerLogo}
                    type="default"
                    hoverAnimation={false}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function getGiftProjection({
  amount,
  fund,
  frequency
}: {
  amount: number
  fund: {
    yearsUntilCollege: number
    collegeEntryYear: number
  }
  frequency: Frequency
}) {
  const newFrequency: 'monthly' | 'annually' | 'onetime' =
    frequency === 'semimonthly' ? 'monthly' : frequency
  const projection = getForecastProjection({
    frequency: newFrequency,
    yearsUntilCollege: fund?.yearsUntilCollege,
    amount
  })
  const projectionString = getForecastProjectionString(projection)

  const infoBubble = (
    <InfoBubble
      iconName="question"
      btnClassName={styles.infoBubble}
      content={generateProjectionDisclaimer(fund)}
      interactive={true}
    />
  )

  if (amount === 0) return 'Add an amount to see how much your gift could grow.'
  if (projection.value === 0) return ''
  return (
    <>
      {NumberFormat.currencyFromCents(amount, false)}{' '}
      {frequencyTextMap[newFrequency]} {projectionString} by{' '}
      {fund.collegeEntryYear} {infoBubble}
    </>
  )
}

type AmountItem = {
  value: number
  label: string
}

type AmountsArray = AmountItem[]

export default GiftSection
